import { useState, useMemo } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ApiDatatable, type ApiDatatableBaseParams } from "@/components/data-table";
import { adminApi } from "@/lib/api/admin-api";
import { companyApi } from "@/lib/api/services/company.api";
import { filterMaterialsByConcept } from "@/lib/materials/concept";
import { useAuth } from "@/hooks/useAuth";
import type { Material, MaterialRow } from "@/types/material";
import { CreateMaterialDialog } from "@/components/recipes/dialogs/CreateMaterialDialog";
import { AddSubIngredientDialog } from "./AddSubIngredientDialog";
import { DeleteMaterialDialog } from "./DeleteMaterialDialog";
import { getMaterialColumns } from "./columns";
import { MaterialTile } from "./MaterialTile";
import {
  IngredientFilterPanel,
  EMPTY_FILTERS,
  type IngredientFilters,
} from "@/components/recipes/detail/tabs/IngredientFilterPanel";

function buildFilterFn(filters: IngredientFilters) {
  return (rows: MaterialRow[]): MaterialRow[] => {
    let result = rows;

    result = filterMaterialsByConcept(result, filters.companyId);

    if (filters.structure.length > 0) {
      result = result.filter((m) => {
        if (filters.structure.includes("compound") && m.type === "COMPOUND") return true;
        if (filters.structure.includes("raw") && m.type === "RAW") return true;
        return false;
      });
    }

    if (filters.costMin !== "") {
      const min = parseFloat(filters.costMin);
      result = result.filter((m) => (m.effectiveUnitCost?.value ?? 0) >= min);
    }
    if (filters.costMax !== "") {
      const max = parseFloat(filters.costMax);
      result = result.filter((m) => (m.effectiveUnitCost?.value ?? Infinity) <= max);
    }

    if (filters.units.length > 0) {
      result = result.filter((m) =>
        filters.units.includes((m.unitOfMeasure ?? "").toLowerCase()),
      );
    }

    if (filters.allergenMode === "with") {
      result = result.filter((m) => (m.allergens?.length ?? 0) > 0);
    } else if (filters.allergenMode === "without") {
      result = result.filter((m) => (m.allergens?.length ?? 0) === 0);
    }

    if (filters.selectedAllergens.length > 0) {
      result = result.filter((m) => {
        const codes = new Set(m.allergens?.map((a) => a.allergen.code) ?? []);
        return filters.selectedAllergens.some((code) => codes.has(code));
      });
    }

    return result;
  };
}

export type IngredientStatus = "all" | "active" | "inactive";

interface MaterialsTabContentProps<TParams extends ApiDatatableBaseParams & { ingredientStatus?: IngredientStatus } = ApiDatatableBaseParams & { ingredientStatus?: IngredientStatus }> {
  searchParams: TParams;
  onNavigate: (updater: (prev: TParams) => TParams) => void;
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
  enabled?: boolean;
  onTotalChange?: (total: number) => void;
}

function toMaterialRow(material: Material): MaterialRow {
  return material as MaterialRow;
}

function getSubRows(material: MaterialRow): MaterialRow[] | undefined {
  if (!material.subIngredients?.length) return undefined;
  return [...material.subIngredients]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((si) => ({
      ...(si.childMaterial as Material),
      _subQty: si.quantity,
      _subUnit: si.unit,
    }));
}

export function MaterialsTabContent<TParams extends ApiDatatableBaseParams & { ingredientStatus?: IngredientStatus } = ApiDatatableBaseParams & { ingredientStatus?: IngredientStatus }>({
  searchParams,
  onNavigate,
  createOpen = false,
  onCreateOpenChange,
  enabled = true,
  onTotalChange,
}: MaterialsTabContentProps<TParams>) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const goToDetail = (material: Material) =>
    void navigate({ to: "/materials/$materialId", params: { materialId: String(material.id) } });
  // Shared-catalogue ingredients (organizationId null) are writable by a super
  // admin only; everyone else gets a read-only row (T-102a).
  const { hasPermission } = useAuth();
  const isSuperAdmin = hasPermission("*:*");

  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [deletingMaterial, setDeletingMaterial] = useState<Material | null>(null);
  const [addingSubTo, setAddingSubTo] = useState<Material | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<IngredientFilters>(EMPTY_FILTERS);

  const filterFn = useMemo(() => buildFilterFn(appliedFilters), [appliedFilters]);

  const { data: companies } = useQuery({
    queryKey: ["companies", "material-concepts"],
    queryFn: () => companyApi.getAll(),
    enabled,
  });
  const availableConcepts = useMemo(
    () => (companies ?? []).filter((c) => c.isActive).map((c) => ({ id: c.id, name: c.name })),
    [companies],
  );

  const toggleMutation = useMutation({
    mutationFn: (material: Material) =>
      adminApi.updateMaterial(material.id, { isActive: !material.isActive }),
    onSuccess: (_, material) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
      toast.success(
        material.isActive ? t`Material set as inactive` : t`Material set as active`,
      );
    },
    onError: () => toast.error(t`Failed to update material status`),
  });

  const columns = getMaterialColumns(
    i18n,
    setEditingMaterial,
    (mat) => toggleMutation.mutate(mat),
    setDeletingMaterial,
    setAddingSubTo,
    isSuperAdmin,
  );

  return (
    <>
      <ApiDatatable
        searchParams={searchParams}
        onNavigate={onNavigate}
        queryKey={["admin", "materials"]}
        queryFn={(params) =>
          adminApi.getMaterials({
            search: params.search,
            page: params.page,
            limit: params.pageSize,
            isActive:
              params.ingredientStatus === "active"
                ? true
                : params.ingredientStatus === "inactive"
                  ? false
                  : undefined,
            includeInactive:
              params.ingredientStatus === undefined || params.ingredientStatus === "all",
          }).then((res) => ({
            data: res.data.map(toMaterialRow),
            meta: res.meta,
          }))
        }
        columns={columns}
        onRowClick={(material) => goToDetail(material as Material)}
        renderTile={(material) => (
          <MaterialTile
            material={material as Material}
            onView={() => goToDetail(material as Material)}
            onEdit={() => setEditingMaterial(material as Material)}
            onToggle={() => toggleMutation.mutate(material as Material)}
            onDelete={() => setDeletingMaterial(material as Material)}
            isSuperAdmin={isSuperAdmin}
          />
        )}
        getRowId={(material) => String(material.id)}
        onTotalChange={onTotalChange}
        getSubRows={getSubRows}
        renderAfterSubRows={(parentRow) => {
          const parent = parentRow.original as MaterialRow;
          const count = parentRow.subRows.length;
          return (
            <div className="flex items-center justify-between">
              <span className="text-sm text-(--color-text-secondary)">
                {t`Showing ${count} ingredient${count !== 1 ? "s" : ""}.`}
              </span>
              <button
                type="button"
                onClick={() => setAddingSubTo(parent)}
                className="flex items-center gap-1.5 text-sm text-(--color-action) hover:underline"
              >
                <Plus className="size-4" />
                {t`Add ingredient`}
              </button>
            </div>
          );
        }}
        enabled={enabled}
        searchPlaceholder={t`Search ingredients…`}
        searchAriaLabel={t`Search ingredients`}
        emptyMessage={t`No ingredients found`}
        filterFn={filterFn}
        toolbarExtra={
          <>
            <Select
              value={searchParams.ingredientStatus ?? "all"}
              onValueChange={(status) =>
                onNavigate((prev) => ({
                  ...prev,
                  ingredientStatus: status as IngredientStatus,
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="w-36" aria-label={t`Filter ingredients by status`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t`All statuses`}</SelectItem>
                <SelectItem value="active">{t`Active`}</SelectItem>
                <SelectItem value="inactive">{t`Inactive`}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              aria-label={t`Refresh ingredients`}
              onClick={() => queryClient.invalidateQueries({ queryKey: ["admin", "materials"] })}
              className="size-8 text-(--color-text-secondary) hover:text-(--color-text-primary)"
            >
              <RefreshCw className="size-4" />
            </Button>
            <IngredientFilterPanel
              filters={appliedFilters}
              onApply={setAppliedFilters}
              availableUnits={[]}
              availableAllergens={[]}
              availableConcepts={availableConcepts}
            />
          </>
        }
      />

      <CreateMaterialDialog
        open={createOpen || !!editingMaterial}
        onOpenChange={(v) => {
          if (!v) {
            setEditingMaterial(null);
            onCreateOpenChange?.(false);
          }
        }}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
          onCreateOpenChange?.(false);
        }}
        editMaterial={editingMaterial ?? undefined}
      />

      {/* Lists the recipes that use the ingredient before it goes (T-1401). */}
      <DeleteMaterialDialog
        material={deletingMaterial}
        onOpenChange={(v) => {
          if (!v) setDeletingMaterial(null);
        }}
      />

      {addingSubTo && (
        <AddSubIngredientDialog
          open
          onOpenChange={(v) => { if (!v) setAddingSubTo(null); }}
          parentMaterial={addingSubTo}
        />
      )}
    </>
  );
}
